/*
 * Extracted from RecommendationRenderer.tsx — party only needs types + toRecommendationModel.
 */
import type { PlayParams } from '@/service/fragments/playParamsFrag'
import { logWarn, type Maybe } from '@amzn/vinyl-util'
import { getLargestImage } from '@/util/imageUtil'
import { ctx } from '@/util/context'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import type { EntityUnion } from '@/service/fragments/entity'

const { target } = ctx('recommendationModel')

export interface RecommendationModel {
    readonly id: Maybe<string>
    readonly playParams: PlayParams | null
    readonly image: ImageFields
    readonly type: RecommendationType
    readonly title: string
    readonly artistName?: Maybe<string>
    readonly link?: Maybe<string>
    readonly node: PlayableEntity
}

export type RecommendationType =
    | 'Album'
    | 'Artist'
    | 'Station'
    | 'Playlist'
    | 'Track'
    | 'PodcastEpisode'
    | 'PodcastShow'

const linkTypeMap = {
    Playlist: 'playlists',
} as const

export function toRecommendationModel(node: EntityUnion): RecommendationModel | null {
    const typename = node.__typename
    switch (typename) {
        case 'Album':
        case 'Artist':
        case 'Station':
        case 'Playlist':
        case 'PodcastShow':
        case 'PodcastEpisode':
        case 'Track': {
            const link =
                typename in linkTypeMap
                    ? `${linkTypeMap[typename as keyof typeof linkTypeMap]}/${node.id}`
                    : null
            const artistName =
                'contributingArtists' in node ? node.contributingArtists?.concatenatedName : null
            const image = getLargestImage(node.images, 'PRIMARY') ?? getLargestImage(node.images)
            if (!image || !node.playParams) return null
            const title = 'title' in node ? node.title : 'name' in node ? node.name : null
            return {
                id: node.id,
                type: typename,
                title: title ?? '',
                artistName,
                image,
                playParams: node.playParams,
                link,
                node: node,
            }
        }
        default: {
            logWarn(target, 'Unknown recommendation type:', typename)
            return null
        }
    }
}
